import type { RouteRecordRaw } from 'vue-router'

/**
 * ★계약 영역 — 라우트 경로 문자열은 변경 금지.
 *
 * 외부 QR 인쇄물, PG사 복귀 URL, LINE LIFF Endpoint가 이 경로에 묶여 있다.
 * 경로를 바꾸면 이미 배포된 QR과 PG 설정이 전부 깨진다.
 * (원본: up-smartorder-web/src/App.js — react-router v5 → vue-router 5 문법 변환만 수행)
 *
 * 변환 규칙: react-router `:param` 은 vue-router 에서도 동일 문법이므로 경로 문자열은 그대로다.
 * 계약 검증은 `__tests__/routes.spec.ts`가 한다.
 */
export const routes: RouteRecordRaw[] = [
  { path: '/', name: 'root', component: () => import('@/pages/root-page.vue') },

  {
    path: '/:companyId/:storeCode/menu-groups/:groupCode/menus/:itemCode',
    name: 'menu-in-group',
    component: () => import('@/pages/menu-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/menus/:itemCode',
    name: 'menu',
    component: () => import('@/pages/menu-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/cart',
    name: 'cart',
    component: () => import('@/pages/cart-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/order',
    name: 'order',
    component: () => import('@/pages/order-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/lite-order',
    name: 'lite-order',
    component: () => import('@/pages/lite-order-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/join',
    name: 'join',
    component: () => import('@/pages/join-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/payment',
    name: 'payment',
    component: () => import('@/pages/payment-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/orders/:orderId/result',
    name: 'result',
    component: () => import('@/pages/result-page.vue'),
  },

  {
    path: '/:companyId/:storeCode/orders/:orderId/complete',
    name: 'order-complete-by-id',
    component: () => import('@/pages/order-complete-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/lite-orders/:orderId/complete',
    name: 'lite-order-complete-by-id',
    component: () => import('@/pages/order-complete-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/order-complete',
    name: 'order-complete',
    component: () => import('@/pages/order-complete-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/orders/:orderId',
    name: 'order-detail',
    component: () => import('@/pages/order-detail-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/lite-orders/:orderId',
    name: 'lite-order-detail',
    component: () => import('@/pages/order-detail-page.vue'),
  },

  {
    path: '/:companyId/:storeCode/errors/payment',
    name: 'payment-error-in-shop',
    component: () => import('@/pages/errors/payment-error-page.vue'),
  },
  {
    path: '/errors/payment',
    name: 'payment-error',
    component: () => import('@/pages/errors/payment-error-page.vue'),
  },
  {
    path: '/:companyId/:storeCode/errors/preparing',
    name: 'preparing',
    component: () => import('@/pages/errors/preparing-page.vue'),
  },
  {
    path: '/service-unavailable',
    name: 'service-unavailable',
    component: () => import('@/pages/errors/service-unavailable-page.vue'),
  },

  {
    path: '/:companyId/:storeCode/user-guide',
    name: 'user-guide',
    component: () => import('@/pages/user-guide-page.vue'),
  },
  {
    path: '/users/:encryptedId/order-stores',
    name: 'user-order-shops',
    component: () => import('@/pages/user-order-shops-page.vue'),
  },
  { path: '/terms', name: 'terms', component: () => import('@/pages/terms-page.vue') },
  { path: '/privacy', name: 'privacy', component: () => import('@/pages/privacy-page.vue') },
  {
    path: '/:companyId/:storeCode/notation',
    name: 'notation',
    component: () => import('@/pages/notation-page.vue'),
  },
  { path: '/qr-order/:id', name: 'qr-order', component: () => import('@/pages/qr-order-page.vue') },

  /*
   * 리뉴얼 신규 — QR 패널 진입점 (원본 계약 경로가 아닌 추가 경로).
   *
   * QR에 실리는 URL이라 최대한 짧게 '/q/<라이선스 키>'로 둔다.
   * '/:companyId/:storeCode'(main)와 같은 2세그먼트지만 정적 세그먼트 'q'가 파라미터보다
   * 높은 점수를 받아 항상 이쪽으로 매칭된다. 따라서 회사코드 'q'는 예약어로 취급해야 한다.
   * 등록완료 화면은 QR에 실리지 않으므로 단축하지 않고 '/kakao-registration'를 그대로 쓴다.
   */
  {
    path: '/q/:licenseKey',
    name: 'kakao-entry',
    component: () => import('@/pages/kakao-entry-page.vue'),
  },
  {
    path: '/kakao-registration',
    name: 'kakao-registration',
    component: () => import('@/pages/kakao-registration-page.vue'),
  },

  // ★ main은 반드시 마지막 — '/:companyId/:storeCode'가 하위 경로를 삼키지 않도록
  {
    path: '/:companyId/:storeCode',
    name: 'main',
    component: () => import('@/pages/main-page.vue'),
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/errors/error-page.vue'),
  },
]

/*
 * 개발 전용 확인 페이지. 프로덕션 번들에는 포함되지 않는다.
 * ★계약 경로가 아니므로 routes.spec.ts의 개수 검증에서도 제외된다.
 */
if (import.meta.env.DEV) {
  routes.splice(routes.length - 1, 0, {
    path: '/__dev/kitchen-sink',
    name: 'dev-kitchen-sink',
    component: () => import('@/pages/dev-kitchen-sink-page.vue'),
  })
}
